/**
 * video-audio-probe.js — reliable "does this video have audio?" detection.
 *
 * The naive approaches all have known false-negatives:
 *   - `videoEl.audioTracks.length`     → empty on Safari/iOS WebKit even when audio exists
 *   - `videoEl.webkitAudioDecodedByteCount` → stays 0 if playback fails silently
 *                                             (iPhone HEVC mp4 trimmed by another tool,
 *                                              files with unusual edit lists, etc.)
 *   - `videoEl.mozHasAudio`            → Firefox-only
 *
 * The reliable answer comes from the CONTAINER, not the decoder:
 *   - mp4-family files → mp4box.js reads moov atom directly
 *   - other containers → FFmpeg probe parses streams
 *
 * Decoder-based heuristics stay as last-resort fallbacks for browsers / contexts
 * where neither container probe is available (mp4box not loaded yet AND FFmpeg
 * WASM not initialized — rare).
 *
 * Bug it closes (2026-04-26): user-reported video-trim case. iPhone HEVC mp4
 * (IMG_3657_cut.mp4) loaded with HTML5 metadata for duration/dimensions but
 * playback silently failed → webkitAudioDecodedByteCount stayed at 0 →
 * "В этом видео нет аудиодорожки" badge shown despite the file having audio.
 * Same false-negative also disabled the waveform extraction code path.
 *
 * Usage:
 *   const result = await window.__detectVideoAudio(file, { videoEl, ffmpeg });
 *   //   → { hasAudio: boolean, source: 'mp4box'|'matroska'|'moov'|'ffmpeg'|'html5_decode'|'audioTracks'|'unknown',
 *   //       confident: boolean, sources: { mp4box?: bool, html5?: bool, ... } }
 *
 *   - hasAudio: best estimate from the highest-priority source that ran
 *   - source: which signal supplied the answer ('mp4box' = container truth)
 *   - confident: true when source is container-level (mp4box / ffmpeg)
 *   - sources: every signal we got, for telemetry / disagreement detection
 *
 * Reusable by all video tools (video-trim, mute-video, video-merge, video-insert,
 * audio-to-video bg-video, etc.) — please don't reimplement audio detection
 * inline in tool blade files. If a new container needs handling, extend this
 * helper, not the call sites.
 */
(function(){
'use strict';
if (window.__detectVideoAudio && !window.__detectVideoAudio.__isStub) return;

var MP4_FAMILY_RE = /\.(mp4|mov|m4v|m4a|3gp)$/i;

async function detectViaMp4Box(file, timeoutMs) {
    if (typeof window.__Mp4BoxProbe !== 'function') return null;
    if (!MP4_FAMILY_RE.test(file.name || '') &&
        !/mp4|quicktime|3gpp/.test(file.type || '')) return null;
    try {
        var info = await Promise.race([
            window.__Mp4BoxProbe(file),
            new Promise(function(_, rej){ setTimeout(function(){ rej(new Error('mp4box timeout')); }, timeoutMs || 1500); })
        ]);
        if (info && typeof info.hasAudio === 'boolean') {
            /* Capture audio codec from container truth (highest priority).
               Keep the QuickTime FourCC table in sync with video-trim.blade.php
               `_audioCodecFromMp4Box`. Without these PCM mappings, mp4box would
               return raw 4-char tags (twos / sowt / in24 / etc.) that no
               consumer recognises → proactive transcode decisions break for
               MOV-from-Apple-devices. */
            if (info.tracks && !_lastProbedCodec) {
                for (var i = 0; i < info.tracks.length; i++) {
                    var t = info.tracks[i];
                    /* See video-trim.blade.php _audioCodecFromMp4Box for the
                       full QuickTime-PCM-as-metadata explanation. Skip only
                       obvious video tracks and detect audio by codec pattern. */
                    if (t.type === 'video') continue;
                    var c = String(t.codec || '').toLowerCase().trim();
                    if      (c.indexOf('mp4a') === 0)     _lastProbedCodec = (c === 'mp4a.6b') ? 'mp3' : 'aac';
                    else if (c === 'ac-3' || c === 'ac3') _lastProbedCodec = 'ac3';
                    else if (c === 'ec-3' || c === 'eac3') _lastProbedCodec = 'eac3';
                    else if (c.indexOf('alac') === 0)     _lastProbedCodec = 'alac';
                    else if (c.indexOf('opus') === 0)     _lastProbedCodec = 'opus';
                    else if (c.indexOf('vorbis') === 0)   _lastProbedCodec = 'vorbis';
                    else if (c.indexOf('mp3') === 0)      _lastProbedCodec = 'mp3';
                    else if (c === 'twos') _lastProbedCodec = 'pcm_s16be';
                    else if (c === 'sowt') _lastProbedCodec = 'pcm_s16le';
                    else if (c === 'in24') _lastProbedCodec = 'pcm_s24be';
                    else if (c === '23ni') _lastProbedCodec = 'pcm_s24le';
                    else if (c === 'in32') _lastProbedCodec = 'pcm_s32be';
                    else if (c === '23in') _lastProbedCodec = 'pcm_s32le';
                    else if (c === 'fl32') _lastProbedCodec = 'pcm_f32be';
                    else if (c === 'fl64') _lastProbedCodec = 'pcm_f64be';
                    else if (c === 'lpcm') _lastProbedCodec = 'pcm_s16le';
                    else if (c === 'raw ' || c === 'raw') _lastProbedCodec = 'pcm_u8';
                    else if (c === 'ulaw') _lastProbedCodec = 'pcm_mulaw';
                    else if (c === 'alaw') _lastProbedCodec = 'pcm_alaw';
                    else if (c) _lastProbedCodec = c;
                    break;
                }
            }
            return info.hasAudio;
        }
    } catch (_) { /* fall through */ }
    return null;
}

/* Matroska / WebM container truth. The EBML walk lives in media-audio-probe.js
   (one owner for that parse — a second copy here would drift); that module is
   per-tool, so it is fetched on first need. Only a container that was READ
   answers: MediaAudioProbe says `hasAudio:null` for anything it could not look
   into, and null here means «let the next signal decide».
   Why it exists (25.09.2026): a WebM clip without a sound track (a sticker, a
   screen capture) had no container verdict at all — mp4box does not read EBML
   and the FFmpeg probe only runs when the engine is already up — so every
   caller walked the full decode waterfall and reported «could not read the
   sound» about a file that simply has none. */
var _mapLoad = null;
function ensureMediaAudioProbe() {
    if (window.MediaAudioProbe && typeof window.MediaAudioProbe.probe === 'function') return Promise.resolve(window.MediaAudioProbe);
    if (!_mapLoad) {
        _mapLoad = new Promise(function (resolve) {
            var done = false;
            function fin() { if (!done) { done = true; resolve(window.MediaAudioProbe || null); } }
            try {
                var s = document.createElement('script');
                s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/media-audio-probe.js') : '/js/media-audio-probe.js';
                s.async = true;
                s.onload = fin;
                s.onerror = function () { _mapLoad = null; fin(); };
                (document.head || document.documentElement).appendChild(s);
                setTimeout(fin, 8000);
            } catch (_) { fin(); }
        });
    }
    return _mapLoad;
}

async function isEbml(file) {
    try {
        var b = new Uint8Array(await file.slice(0, 4).arrayBuffer());
        return b.length === 4 && b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3;
    } catch (_) { return false; }
}

/* audioCodec is promised in FFmpeg's names (video-trim compares it with
   its copy lists). Carry only names that map exactly: 'pcm' says
   nothing about the sample format, so it gives no name at all rather
   than a guessed pcm_s16le that could green-light a wrong -c copy. */
var FF_NAMES = { 'aac': 'aac', 'opus': 'opus', 'vorbis': 'vorbis', 'mp3': 'mp3', 'mp2': 'mp2',
                 'flac': 'flac', 'alac': 'alac', 'ac-3': 'ac3', 'e-ac-3': 'eac3', 'dts': 'dts', 'truehd': 'truehd' };

/* One MediaAudioProbe answer, kept only when it READ the container `source`. */
async function detectViaWalk(file, source) {
    var M = await ensureMediaAudioProbe();
    if (!M) return null;
    try {
        var r = await M.probe(file);
        if (!r || r.source !== source || typeof r.hasAudio !== 'boolean') return null;
        var c0 = r.hasAudio && r.codecs && r.codecs[0] ? FF_NAMES[String(r.codecs[0])] : '';
        if (c0 && !_lastProbedCodec) _lastProbedCodec = c0;
        return r.hasAudio;
    } catch (_) { return null; }
}

async function detectViaMatroska(file) {
    if (!file || typeof file.slice !== 'function') return null;
    if (!(await isEbml(file))) return null;
    return detectViaWalk(file, 'mkv');
}

/* mp4/mov when mp4box did not answer. Its 1.5 s race includes loading the
   library itself (~160 KB), so the FIRST video a page asks about came back
   «unknown → has audio» — measured 20.10.2026 on Yandex Browser: 1508 ms for
   the first file, 24 ms for the next. A clip with no sound track then went on
   to the decoder as if it had one (/split-screen: the export decoded it and
   the tab died). MediaAudioProbe reaches moov by hopping top-level boxes —
   no library, two or three small reads. */
async function isIsoBmff(file) {
    try {
        var b = new Uint8Array(await file.slice(0, 8).arrayBuffer());
        if (b.length < 8) return false;
        var t = String.fromCharCode(b[4], b[5], b[6], b[7]);
        return /^(ftyp|moov|mdat|wide|free|skip)$/.test(t);
    } catch (_) { return false; }
}

async function detectViaMoovWalk(file) {
    if (!file || typeof file.slice !== 'function') return null;
    if (!(await isIsoBmff(file))) return null;
    return detectViaWalk(file, 'mp4');
}

/* Side-channel for codec extraction. detectViaFFmpegProbe and
   detectViaMp4Box already parse stream metadata; capturing the codec name
   here costs ~one regex per probe and lets video-trim (and any other
   container-aware tool) make proactive transcode decisions instead of
   reactive retry-after-fail. Lowercase ffmpeg-style names ("aac", "ac3",
   "pcm_s16le", "mp3", "opus", "vorbis", "alac"). */
var _lastProbedCodec = '';

async function detectViaFFmpegProbe(file, opts) {
    var fp = (opts && opts.ffmpeg) || window.FFmpegProcessor;
    if (!fp || typeof fp.probe !== 'function') return null;
    /* Only fast-path: skip lengthy WASM init just for an audio probe. If the
       caller already initialized FFmpeg (.isReady() true) we can probe in
       ~100-300 ms. Otherwise return null and let lower-priority signals decide. */
    if (typeof fp.isReady === 'function' && !fp.isReady()) return null;
    try {
        /* 16 MB header slice is plenty for stream descriptors. */
        var probeSize = Math.min(file.size, 16 * 1024 * 1024);
        var slice = probeSize < file.size
            ? new File([file.slice(0, probeSize)], file.name, { type: file.type })
            : file;
        var log = await fp.probe(slice);
        var codecMatch = log.match(/Audio:\s*([a-z0-9_]+)/i);
        if (codecMatch && !_lastProbedCodec) _lastProbedCodec = codecMatch[1].toLowerCase();
        /* ffmpeg.wasm (5.1) prints the mp4 track id first: `#0:1[0x2](und): Audio:`.
           The id was not allowed here, and every mp4 this branch answered for
           came back «no audio», confidently (30.09.2026 — /video-volume on a
           cold load: the loudness «—» and a safety limiter on the export). */
        return /Stream\s+#\d+:\d+(\[[^\]]*\])?(\([^\)]*\))?:\s*Audio:/i.test(log);
    } catch (_) { /* fall through */ }
    return null;
}

async function detectViaDecodedBytes(videoEl) {
    /* WebKit-specific. The catch: this requires successful playback. If the
       browser fails to decode the video stream, audio decode also fails →
       byte count stays 0 → false negative. Only useful when we have no
       container-level signal. */
    if (!videoEl || typeof videoEl.webkitAudioDecodedByteCount === 'undefined') return null;
    var prevMuted = videoEl.muted;
    var prevTime = videoEl.currentTime;
    videoEl.muted = true;
    try {
        var playPromise = videoEl.play();
        if (playPromise && typeof playPromise.then === 'function') {
            await playPromise.catch(function(){});
        }
        for (var i = 0; i < 8; i++) {
            if (videoEl.webkitAudioDecodedByteCount > 0) break;
            await new Promise(function(r){ setTimeout(r, 150); });
        }
        var bytes = videoEl.webkitAudioDecodedByteCount || 0;
        videoEl.pause();
        try { videoEl.currentTime = prevTime; } catch (_){}
        videoEl.muted = prevMuted;
        return bytes > 0;
    } catch (_) {
        try { videoEl.pause(); } catch (_){}
        videoEl.muted = prevMuted;
        return null;
    }
}

function detectViaAudioTracks(videoEl) {
    if (!videoEl || !videoEl.audioTracks) return null;
    /* On iOS Safari/Chrome-on-iOS, audioTracks is *defined* but empty even when
       audio exists — that's why this signal is last-priority. We only return
       a value when audioTracks.length > 0 (definitely has audio); never use
       it to claim "no audio" without a corroborating signal. */
    if (videoEl.audioTracks.length > 0) return true;
    return null;
}

window.__detectVideoAudio = async function(file, opts) {
    opts = opts || {};
    var videoEl = opts.videoEl || null;
    var sources = {};
    _lastProbedCodec = ''; // reset per call

    /* 1. Container truth — mp4box for mp4-family. */
    var mp4boxResult = await detectViaMp4Box(file, opts.mp4boxTimeoutMs);
    if (mp4boxResult !== null) sources.mp4box = mp4boxResult;

    /* 1b. Container truth — Matroska / WebM (EBML Tracks). */
    if (mp4boxResult === null) {
        var mkvResult = await detectViaMatroska(file);
        if (mkvResult !== null) sources.matroska = mkvResult;
        else {
            /* 1c. Container truth — mp4/mov moov walk, when mp4box was late or absent. */
            var moovResult = await detectViaMoovWalk(file);
            if (moovResult !== null) sources.moov = moovResult;
        }
    }

    /* 2. FFmpeg probe — only if engine is already loaded (no extra init cost),
       and only when the container has not answered: container truth outranks
       it below anyway, and the probe copies the first 16 MB of the file into
       the engine per call — per clip, on a deck of a dozen phone videos. */
    var ffmpegResult = ('mp4box' in sources || 'matroska' in sources || 'moov' in sources) ? null : await detectViaFFmpegProbe(file, opts);
    if (ffmpegResult !== null) sources.ffmpeg = ffmpegResult;

    /* 3. WebKit decoded-bytes — lossy on iOS HEVC + edge edits, but free signal. */
    if (videoEl && opts.skipPlayback !== true) {
        var bytesResult = await detectViaDecodedBytes(videoEl);
        if (bytesResult !== null) sources.html5_decode = bytesResult;
    }

    /* 4. audioTracks — only as a positive-signal corroborator. */
    var atResult = videoEl ? detectViaAudioTracks(videoEl) : null;
    if (atResult !== null) sources.audioTracks = atResult;

    /* Decide the authoritative answer in priority order. */
    var hasAudio, source, confident;
    if ('mp4box' in sources)         { hasAudio = sources.mp4box;        source = 'mp4box';        confident = true; }
    else if ('matroska' in sources)  { hasAudio = sources.matroska;      source = 'matroska';      confident = true; }
    else if ('moov' in sources)      { hasAudio = sources.moov;          source = 'moov';          confident = true; }
    else if ('ffmpeg' in sources)    { hasAudio = sources.ffmpeg;        source = 'ffmpeg';        confident = true; }
    else if ('html5_decode' in sources){ hasAudio = sources.html5_decode;source = 'html5_decode';  confident = false; }
    else if ('audioTracks' in sources){ hasAudio = sources.audioTracks;  source = 'audioTracks';   confident = false; }
    else                             { hasAudio = true;                  source = 'unknown';       confident = false; }
    /* "unknown" defaults to true — false-positive (showing waveform UI for a
       video with no audio) is recoverable; false-negative (hiding audio that
       exists) is what got us into this bug. */

    return {
        hasAudio: hasAudio,
        source: source,
        confident: confident,
        sources: sources,
        /* Audio codec name (lowercase ffmpeg-style) — populated from whichever
           probe ran. Empty string if no probe yielded codec info. Tools can use
           this for proactive transcode decisions; ignore if unset. */
        audioCodec: _lastProbedCodec,
    };
};

})();
